import {useState} from 'react';
import {
    Alert,
    Badge,
    Card,
    Code,
    Collapse,
    Divider,
    Grid,
    Group,
    SimpleGrid,
    Stack,
    Text,
    Timeline,
    Title,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import {
    AiOutlineBuild,
    AiOutlineCloudUpload,
    AiOutlineCode,
    AiOutlineExclamationCircle,
    AiOutlineExperiment,
    AiOutlineFileText,
    AiOutlineInbox,
    AiOutlineInfoCircle,
} from 'react-icons/ai';
import {
    AnalysisFinding,
    AnalysisRecommendation,
    PipelineStage,
    RepositoryAnalysis,
} from '../types';
import {AnalysisFindingsList} from './AnalysisFindingsList';
import {groupFindingsByDecision, severityColor} from './analysisFindingGroups';

const confidenceColor = (confidence: string) => {
    switch (confidence) {
        case 'HIGH':
            return 'green';
        case 'MEDIUM':
            return 'yellow';
        case 'LOW':
            return 'red';
        default:
            return 'gray';
    }
};

/**
 * Confidence is stated in terms of the evidence that produced it — never as a
 * promise of correctness. Every level tells the admin to review, because every
 * suggestion is an editable default.
 */
const confidenceHelp: Record<string, string> = {
    HIGH: 'Every suggestion below is backed by an explicit file in the repository. Review the evidence and change anything that is wrong.',
    MEDIUM: 'Some suggestions come from partial evidence or defaults. Read the evidence for each decision before provisioning.',
    LOW: 'Little direct evidence was found; several suggestions are heuristic fallbacks. Expect to change them.',
};

const stageKindIcon = (kind?: string) => {
    switch (kind) {
        case 'UNIT_TEST':
            return <AiOutlineExperiment/>;
        case 'BUILD':
            return <AiOutlineBuild/>;
        case 'ASSETS':
            return <AiOutlineInbox/>;
        case 'DEPLOY':
            return <AiOutlineCloudUpload/>;
        default:
            return <AiOutlineCode/>;
    }
};

const categorizeFile = (file: string): string => {
    const lower = file.toLowerCase();
    if (lower.includes('buildspec') || lower.includes('codebuild')) return 'Build';
    if (lower.includes('cdk.json') || lower.includes('cloudformation') || lower.endsWith('.template.json') || lower.endsWith('.template.yaml')) return 'Infrastructure';
    if (lower === 'package.json' || lower === 'pnpm-lock.yaml' || lower === 'yarn.lock' || lower === 'package-lock.json') return 'Package';
    if (lower.endsWith('.yml') || lower.endsWith('.yaml')) return 'Configuration';
    if (lower === 'dockerfile' || lower.startsWith('dockerfile.')) return 'Container';
    return 'Other';
};

const DetailRow = ({label, children}: { label: string; children: React.ReactNode }) => (
    <Stack gap={2}>
        <Text size='xs' c='dimmed' fw={500} tt='uppercase'>{label}</Text>
        <Text size='sm'>{children}</Text>
    </Stack>
);

const RecommendationsSection = ({items}: { items: AnalysisRecommendation[] }) => {
    if (!items.length) return null;

    const warnings = items.filter(i => i.severity === 'WARNING');
    const infos = items.filter(i => i.severity === 'INFO');
    const ordered = [...warnings, ...infos];

    return (
        <Stack gap='xs'>
            <Group gap='xs'>
                <Title order={5}>Recommendations for the repository</Title>
                <Badge size='sm' variant='light'>{items.length}</Badge>
            </Group>
            {ordered.map((rec) => (
                <Alert
                    key={rec.key}
                    color={severityColor(rec.severity)}
                    icon={rec.severity === 'WARNING' ? <AiOutlineExclamationCircle/> : <AiOutlineInfoCircle/>}
                    variant='light'
                >
                    <Stack gap='xs'>
                        <Text size='sm' fw={500}>{rec.message}</Text>
                        {rec.suggestion && (
                            <Text size='xs' c='dimmed'>{rec.suggestion}</Text>
                        )}
                    </Stack>
                </Alert>
            ))}
        </Stack>
    );
};

/**
 * The analyzer's reasoning, shown expanded by default and grouped by the
 * question each finding answers. This is the transparency requirement of
 * FNP-1017: no hidden runbooks, no implied decisions — the admin can see what
 * was inspected, what it said, and what was concluded from it.
 */
const ReasoningSection = ({findings}: { findings: AnalysisFinding[] }) => {
    if (!findings.length) return null;

    const groups = groupFindingsByDecision(findings);
    const warningCount = findings.filter(f => f.severity === 'WARNING').length;

    return (
        <Stack gap='sm'>
            <Group gap='xs'>
                <Title order={5}>Why these suggestions?</Title>
                <Badge size='sm' variant='light'>{findings.length} findings</Badge>
                {warningCount > 0 && (
                    <Badge size='sm' variant='light' color='yellow'>
                        {warningCount} needing review
                    </Badge>
                )}
            </Group>
            <Text size='xs' c='dimmed'>
                Each line shows what the analyzer inspected, the evidence it found, and
                the conclusion it drew. Nothing below is applied until you provision.
            </Text>
            <Stack gap='md'>
                {groups.map((group) => (
                    <Card key={group.id} withBorder padding='sm' radius='sm'>
                        <Stack gap='sm'>
                            <Text size='sm' fw={600}>{group.label}</Text>
                            <AnalysisFindingsList
                                findings={group.findings}
                                aria-label={group.label}
                            />
                        </Stack>
                    </Card>
                ))}
            </Stack>
        </Stack>
    );
};

const StagesTimeline = ({stages}: { stages: PipelineStage[] }) => {
    const sorted = [...stages].sort((a, b) => a.order - b.order);

    return (
        <Stack gap='xs'>
            <Group gap='xs'>
                <Title order={5}>Suggested stages</Title>
                <Badge size='sm' variant='outline' color='gray'>editable</Badge>
            </Group>
            <Text size='xs' c='dimmed'>
                These stages are a starting point. Adjust them in the form before
                provisioning.
            </Text>
            <Timeline active={sorted.length - 1} bulletSize={28} lineWidth={2}>
                {sorted.map((stage, i) => (
                    <Timeline.Item
                        key={i}
                        bullet={stageKindIcon(stage.stageKind)}
                        title={<Text fw={500}>{stage.name}</Text>}
                    >
                        <Group gap='xs' mt={4}>
                            {stage.stageKind && (
                                <Badge size='xs' variant='light'>{stage.stageKind}</Badge>
                            )}
                            {stage.buildProvider && (
                                <Badge size='xs' variant='outline' color='blue'>
                                    build: {stage.buildProvider}
                                </Badge>
                            )}
                            {stage.deployProvider && (
                                <Badge size='xs' variant='outline' color='grape'>
                                    deploy: {stage.deployProvider}
                                </Badge>
                            )}
                            {stage.requiresApproval && (
                                <Badge size='xs' color='yellow'>approval</Badge>
                            )}
                        </Group>
                    </Timeline.Item>
                ))}
            </Timeline>
        </Stack>
    );
};

const DeploymentAccountsGrid = ({accounts}: {
    accounts: NonNullable<RepositoryAnalysis['suggestedDeploymentAccounts']>
}) => (
    <Stack gap='xs'>
        <Title order={5}>Suggested deployment accounts</Title>
        <SimpleGrid cols={{base: 1, sm: 2}} spacing='xs'>
            {accounts.map((a, i) => (
                <Card key={i} withBorder padding='sm' radius='sm'>
                    <Stack gap={2}>
                        <Text size='sm' fw={500}>
                            {a.accountName ?? a.accountId}
                        </Text>
                        {a.accountName && (
                            <Text size='xs' c='dimmed' ff='monospace'>{a.accountId}</Text>
                        )}
                        {a.region && (
                            <Text size='xs' c='dimmed'>region: {a.region}</Text>
                        )}
                        {a.deploymentBucketName && (
                            <Text size='xs' c='dimmed' ff='monospace'>
                                bucket: {a.deploymentBucketName}
                            </Text>
                        )}
                    </Stack>
                </Card>
            ))}
        </SimpleGrid>
    </Stack>
);

const DetectedFilesSection = ({files}: { files: string[] }) => {
    const [expanded, setExpanded] = useState(false);

    const grouped = files.reduce<Record<string, string[]>>((acc, f) => {
        const cat = categorizeFile(f);
        (acc[cat] ||= []).push(f);
        return acc;
    }, {});

    const categories = Object.keys(grouped).sort();
    const shouldCollapse = files.length > 5;
    const open = !shouldCollapse || expanded;

    const heading = (
        <Group gap='xs'>
            <Title order={5}>
                <Group gap={6} display='inline-flex'>
                    <AiOutlineFileText/>
                    Files inspected
                </Group>
            </Title>
            <Badge size='sm' variant='light'>{files.length}</Badge>
            {shouldCollapse && (
                <Text size='xs' c='dimmed'>
                    {expanded ? 'Collapse' : 'Expand'}
                </Text>
            )}
        </Group>
    );

    return (
        <Stack gap='xs'>
            {shouldCollapse ? (
                <UnstyledButton
                    onClick={() => setExpanded(e => !e)}
                    aria-expanded={expanded}
                    aria-controls='analysis-detected-files'
                >
                    {heading}
                </UnstyledButton>
            ) : heading}
            <Collapse in={open} id='analysis-detected-files'>
                <Stack gap='xs'>
                    {categories.map(cat => (
                        <Group key={cat} gap='xs' align='flex-start' wrap='wrap'>
                            <Badge size='sm' variant='outline' style={{minWidth: 110}}>
                                {cat}
                            </Badge>
                            <Group gap={6} wrap='wrap'>
                                {grouped[cat].map((f) => (
                                    <Code key={f}>{f}</Code>
                                ))}
                            </Group>
                        </Group>
                    ))}
                </Stack>
            </Collapse>
        </Stack>
    );
};

interface AnalysisResultPanelProps {
    analysis: RepositoryAnalysis;
}

export const AnalysisResultPanel = ({analysis}: AnalysisResultPanelProps) => {

    const findings = analysis.findings ?? [];
    const recommendations = analysis.recommendations ?? [];
    const stages = analysis.suggestedStages ?? [];
    const accounts = analysis.suggestedDeploymentAccounts ?? [];
    const files = analysis.detectedFiles ?? [];

    return (
        <Stack gap='lg'>
            {/* Header card: confidence + repo + suggested name */}
            <Card withBorder padding='md' radius='md'>
                <Group justify='space-between' align='flex-start' wrap='nowrap'>
                    <Stack gap={4}>
                        <Text size='xs' c='dimmed' tt='uppercase' fw={500}>
                            Suggested pipeline
                        </Text>
                        <Title order={3}>{analysis.suggestedPipelineName}</Title>
                        <Text size='sm' c='dimmed'>
                            {analysis.repositoryName} · {analysis.branchName}
                        </Text>
                    </Stack>
                    <Tooltip
                        label={confidenceHelp[analysis.confidence] ?? ''}
                        multiline
                        w={260}
                        withArrow
                    >
                        <Badge
                            size='lg'
                            variant='light'
                            color={confidenceColor(analysis.confidence)}
                            leftSection={<AiOutlineInfoCircle/>}
                            tabIndex={0}
                            aria-label={`Evidence strength ${analysis.confidence}. ${confidenceHelp[analysis.confidence] ?? ''}`}
                        >
                            {analysis.confidence} evidence
                        </Badge>
                    </Tooltip>
                </Group>
            </Card>

            <Alert
                variant='light'
                color='blue'
                icon={<AiOutlineInfoCircle/>}
                title='Nothing has been created yet'
            >
                <Text size='sm'>
                    This is a suggested starting point derived from the repository, not a
                    decision. Every value below is an editable default — review the
                    evidence for each one and change anything that is wrong before you
                    provision.
                </Text>
            </Alert>

            {/* Summary grid */}
            <Card withBorder padding='md' radius='md'>
                <Grid gutter='md'>
                    <Grid.Col span={{base: 12, sm: 6}}>
                        <DetailRow label='Pipeline type'>
                            <Badge variant='light'>{analysis.suggestedPipelineType}</Badge>
                        </DetailRow>
                    </Grid.Col>
                    {analysis.detectedArchetype && (
                        <Grid.Col span={{base: 12, sm: 6}}>
                            <DetailRow label='Detected archetype'>
                                <Badge variant='light' color='grape'>
                                    {analysis.detectedArchetype}
                                </Badge>
                            </DetailRow>
                        </Grid.Col>
                    )}
                    {analysis.suggestedTemplateName && (
                        <Grid.Col span={{base: 12, sm: 6}}>
                            <DetailRow label='Template'>
                                {analysis.suggestedTemplateName}
                            </DetailRow>
                        </Grid.Col>
                    )}
                    {analysis.suggestedBuildCommand && (
                        <Grid.Col span={12}>
                            <DetailRow label='Build command'>
                                <Code>{analysis.suggestedBuildCommand}</Code>
                            </DetailRow>
                        </Grid.Col>
                    )}
                </Grid>
            </Card>

            {findings.length > 0 && (
                <>
                    <ReasoningSection findings={findings}/>
                    <Divider/>
                </>
            )}

            {recommendations.length > 0 && (
                <>
                    <RecommendationsSection items={recommendations}/>
                    <Divider/>
                </>
            )}

            {stages.length > 0 && (
                <>
                    <StagesTimeline stages={stages}/>
                    <Divider/>
                </>
            )}

            {accounts.length > 0 && (
                <>
                    <DeploymentAccountsGrid accounts={accounts}/>
                    <Divider/>
                </>
            )}

            {files.length > 0 && <DetectedFilesSection files={files}/>}
        </Stack>
    );
};
